import { useState } from "react";
import useConversations, { Conversation } from "./useConversations";
import BasicTableView from "../../supportal/CloudListView/BasicTableView";
import dayjs from "dayjs";
import ViewRecordDrawer from "../../../components/Drawers/Records/ViewRecordDrawer";
import useModule from "../../../hooks/useModule";
import CommentsDrawer from "../../../components/Drawers/CommentsDrawer/CommentsDrawer";
import BasicToolbar from "./BasicToolbar";
import { Query } from "../../../types/GlobalInterfaces";
import StrUtils from "../../../utils/StrUtils";
import pluralize from "pluralize";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import { useSearchParams } from "react-router-dom";

const QueryFields = [
  "ContextID",
  "Context",
  "Participants",
  "LastUpdated",
  "RecordName",
  "RecordModule",
];

export function InitialConversationsQuery(): Query {
  return {
    Fields: QueryFields,
    OrderBy: { Field: "LastUpdated", Direction: "DESC" },
    Filters: [],
    Pagination: { Page: 1, Count: 25 },
  };
}

export default function ConversationsPage() {
  const [query, setQuery] = useState(InitialConversationsQuery());
  const [searchQuery, setSearchQuery] = useState<Query | null>(null);
  const { module: convoMod } = useModule("Conversations");

  const { conversations, pagination, conversationsLoading } = useConversations(
    searchQuery || query
  );

  return (
    <div className="mt-4">
      <BasicToolbar
        query={query}
        setQuery={setQuery}
        mod={convoMod}
        onSearch={onSearch}
      />
      <ConversationsList
        conversations={conversations}
        conversationsLoading={conversationsLoading}
      />
      <div className="mx--3 mx-md--4">
        <AppTablePagination
          records={conversations}
          pagination={pagination}
          query={query ? query : searchQuery}
          setQuery={query ? setQuery : setSearchQuery}
          error={null}
          loading={conversationsLoading}
          module={convoMod}
          sticky
        />
      </div>
    </div>
  );

  function onSearch(newSearch: string) {
    if (!newSearch) return setSearchQuery(null);

    setSearchQuery({
      Fields: QueryFields,
      Filters: [
        {
          Group: true,
          Filters: [
            { Field: "RecordName", Method: "contains", Value: newSearch },
          ],
        },
        {
          Group: true,
          Filters: [
            {
              Field: "RecordModule",
              Method: "contains",
              Value: StrUtils.cleanAPIName(newSearch),
            },
          ],
        },
      ],
      Pagination: { Page: 1, Count: 25 },
    });
  }
}

export function ConversationsList({
  conversations,
  conversationsLoading,
  maxWidth,
}: {
  conversations: Conversation[];
  conversationsLoading: boolean;
  maxWidth?: number | string;
}) {
  const [search, setSearch] = useSearchParams();
  const [recordOpened, setRecordOpened] = useState(false);
  const convoRecordID = search.get("recordID");
  const convoRecordName = search.get("recordName");
  const convoRecordModule = search.get("recordModule");
  const open = !!search.get("open");
  const commentsDrawer = !!search.get("commentsDrawer");

  const { module: mod } = useModule(convoRecordModule || undefined);

  return (
    <>
      <BasicTableView
        emptyMessage="No Conversations found."
        containerCSS="mt-3 mb-3"
        title={undefined}
        loading={conversationsLoading}
        items={conversations || []}
        getItemIcon={() => (
          <i
            className="text-xl mr-3 mdi mdi-forum-outline"
            style={{ lineHeight: 1 }}
          />
        )}
        getItemTitle={(c) => {
          let title: React.ReactNode = "Conversation";
          const participantCount = c?.Participants?.length;

          if (c.Context === "Record" && c.RecordName) {
            title = (
              <div
                className="font-weight-500 text-truncate"
                style={{ maxWidth: maxWidth }}
              >
                Conversation about{" "}
                <span
                  className="text-primary font-weight-600"
                  style={{ paddingLeft: 1, paddingRight: 1 }}
                >
                  {c.RecordName}
                </span>{" "}
                {participantCount > 1
                  ? `with ${participantCount - 1} ${pluralize(
                      "other",
                      participantCount - 1
                    )}`
                  : ""}
              </div>
            );
          }

          return title;
        }}
        getItemSubtitle={(c) => {
          if (!c.LastUpdated) return "";

          const lastUpdated = dayjs(c?.LastUpdated).format(
            "MMMM Do YYYY, h:mm a"
          );

          return `Last updated on ${lastUpdated}`;
        }}
        onClick={(c) => {
          if (c.Context === "Record") {
            if (!c.RecordModule) return;

            setSearch((prev) => ({
              ...prev,
              recordID: c.ContextID,
              recordModule: c.RecordModule,
              recordName: c.RecordName,
              open: true,
              commentsDrawer: true,
            }));
          }
        }}
      />
      {convoRecordID && mod && (
        <ViewRecordDrawer
          open={open}
          onOpened={() => setRecordOpened(true)}
          onClose={() =>
            setSearch((prev) => {
              prev.delete("open");
              return prev;
            })
          }
          onExited={() => {
            setSearch({});
            setRecordOpened(false);
          }}
          recordID={convoRecordID}
          module={mod}
        />
      )}
      {convoRecordID && convoRecordName && convoRecordModule && mod && (
        <CommentsDrawer
          open={recordOpened && commentsDrawer}
          onClose={() =>
            setSearch((prev) => {
              prev.delete("commentsDrawer");
              return prev;
            })
          }
          context="Record"
          contextID={convoRecordID}
          convoMetadata={{
            RecordName: convoRecordName,
            RecordModule: convoRecordModule,
          }}
        />
      )}
    </>
  );
}
